import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { connect, useSelector } from 'react-redux'

import { isEmpty } from 'lodash-es'
import styled from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  appointmentUtilization,
  ProviderAdminProviderInfo,
  targetUtilization,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { colors } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/colors'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import RecommendationExplanationContent from './RecommendationExplanationContent'
import { track } from '../../../../mixpanel/mixpanelTracking'
import AvailabilityCard from '../../common/components/availabilityCard/AvailabilityCard'
import { employmentTypes } from '../../common/constants/appConstants'
import { getAuthUser } from '../../data/auth/authSelectors'
import { getRequestPaymentSelectedProvider } from '../../data/requestPayment/requestPaymentSelectors'
import { RootState } from '../../data/store'
import { useCapacityUpdateFlow } from '../../hooks/useCapacityUpdateFlow'

const Container = styled.View({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const TitleInnerContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['4px'],
  alignItems: 'center',
}))

const TooltipContainer = styled.View({
  margin: 'auto',
})

const ButtonContainer = styled.View({
  margin: 'auto 0',
})

export const CapacityHeader: React.FC<CapacityHeaderProps> = ({
  targetUtilization,
  appointmentUtilization,
  selectedProvider,
  showMinified,
}) => {
  const user = useSelector(getAuthUser)
  const { formatMessage } = useIntl()
  const { showCalendarAvailabilitiesWithCapacity } = useFlags()
  const { openUpdateFlow, modals } = useCapacityUpdateFlow()

  type ModalContent = {
    body: React.ReactNode
    isLarge: boolean
  }
  const provider = selectedProvider || user
  const [modalContent, setModalContent] = useState<ModalContent>({} as ModalContent)
  const upcomingWeekSessionTarget = targetUtilization?.upcomingWeekSessionTarget ?? 0
  const upcomingWeekSessionTargetPlus = targetUtilization?.upcomingWeekSessionTargetPlus ?? 0
  const upcomingWeekPaidTimeOff = targetUtilization?.upcomingWeekPaidTimeOff ?? 0
  const { scheduledNextWeek } = appointmentUtilization
  const cancellationBuffer = provider.employment_status === employmentTypes.FULL_TIME ? 4 : 2

  let currentTargetLevel: string, sessionsNeededToReachTargetLevel: number, targetInfoSentence: string
  if (upcomingWeekSessionTarget > scheduledNextWeek) {
    currentTargetLevel = 'Target'
    sessionsNeededToReachTargetLevel = upcomingWeekSessionTarget - scheduledNextWeek + cancellationBuffer
    targetInfoSentence = `Add ${sessionsNeededToReachTargetLevel} sessions to reach ${currentTargetLevel} next week`
  } else if (upcomingWeekSessionTargetPlus > scheduledNextWeek) {
    currentTargetLevel = 'Target Plus'
    sessionsNeededToReachTargetLevel = upcomingWeekSessionTargetPlus - scheduledNextWeek + cancellationBuffer
    targetInfoSentence = `Add ${sessionsNeededToReachTargetLevel} sessions to reach ${currentTargetLevel} next week`
  } else {
    currentTargetLevel = 'Target Plus'
    targetInfoSentence = `Great! You're above ${currentTargetLevel} next week`
  }

  return (
    <div>
      <Container>
        <div>
          {!showMinified && (
            <>
              <TitleInnerContainer>
                <Title text={targetInfoSentence} size={TitleSize.LARGE} color={colors.ui_oatmeal6} />
                <TooltipContainer>
                  <Tooltip
                    hoverEnabled
                    content={
                      'This dashboard is for planning purposes only. Your performance-based compensation will be calculated by operations at the end of the quarter.'
                    }
                    placement='right'
                  >
                    <InfoIcon size={18} />
                  </Tooltip>
                </TooltipContainer>
              </TitleInnerContainer>
              <Link
                text='How do we calculate this?'
                onPress={() => {
                  setModalContent({
                    body: (
                      <RecommendationExplanationContent
                        upcomingWeekSessionTarget={
                          upcomingWeekSessionTarget > scheduledNextWeek
                            ? upcomingWeekSessionTarget
                            : upcomingWeekSessionTargetPlus
                        }
                        targetLevel={currentTargetLevel}
                        employmentType={provider.employment_status || ''}
                        scheduledNextWeek={scheduledNextWeek}
                        nextWeekSessionRecommendation={sessionsNeededToReachTargetLevel}
                        upcomingWeekPaidTimeOff={upcomingWeekPaidTimeOff}
                      />
                    ),
                    isLarge: true,
                  })
                  track({
                    event: 'SHOW_RECOMMENDATION_EXPLANATION_MODAL',
                  })
                }}
                testID={tID('CapacityHeader-explanation-button')}
              />
            </>
          )}
        </div>
        {showCalendarAvailabilitiesWithCapacity ? (
          <></>
        ) : showMinified ? (
          <AvailabilityCard isMinified />
        ) : (
          <ButtonContainer>
            <PrimaryButton
              testID={tID('CapacityHeader-update-button')}
              text={formatMessage({
                defaultMessage: 'Update availability',
                description: 'button to update provider availabity for new clients',
              })}
              onPress={openUpdateFlow}
            />
          </ButtonContainer>
        )}
      </Container>
      <Modal
        visible={!isEmpty(modalContent)}
        modalContents={
          Object.prototype.hasOwnProperty.call(modalContent, 'body') ? (
            (modalContent.body as React.ReactElement)
          ) : (
            <div />
          )
        }
        onRequestClose={() => setModalContent({} as ModalContent)}
        onCloseEnd={() => setModalContent({} as ModalContent)}
        scrollable
        scrollableModalHeight='auto'
        scrollableModalWidth='700px'
        isHeaderTransparent
        disableBottomSheet
      />
      {modals}
    </div>
  )
}

type CapacityHeaderProps = {
  targetUtilization?: targetUtilization
  appointmentUtilization: appointmentUtilization
  selectedProvider: ProviderAdminProviderInfo
  showMinified: boolean
}

const mapStateToProps = (state: RootState) => {
  return {
    selectedProvider: getRequestPaymentSelectedProvider(state),
  }
}

export default connect(mapStateToProps)(CapacityHeader)
